import omit from 'lodash/omit';
import {
  Children,
  cloneElement,
  isValidElement,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import Markdown, { defaultUrlTransform } from 'react-markdown';
import type { Components } from 'react-markdown';
import { Streamdown, type MermaidErrorComponentProps } from 'streamdown';
import { code } from '@streamdown/code';
import { mermaid } from '@streamdown/mermaid';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChatMermaidPreview from './chat-mermaid-preview';
import 'streamdown/styles.css';

/** Allow data/blob image URLs; default transform strips them. */
function chatMarkdownUrlTransform(url: string): string {
  const t = String(url ?? '').trim();
  if (!t) return t;
  if (/^data:image\//i.test(t)) return t;
  if (/^blob:/i.test(t)) return t;
  if (/^https?:\/\//i.test(t)) return t;
  return defaultUrlTransform(url);
}

function normalizeUrlProp(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (Array.isArray(value)) {
    const first = value.find((v) => v != null && String(v).trim() !== '');
    return first != null ? String(first) : undefined;
  }
  const s = String(value).trim();
  return s === '' ? undefined : s;
}

const INNER_HTML_LANGS = new Set(['html', 'htm', 'xml']);

function isDomMediaSnippet(t: string): boolean {
  const s = t.trim();
  if (!s) return false;
  return /^<\s*(img|svg|picture|video|iframe)\b/i.test(s);
}

function languageFromClassName(className: string | undefined): string | null {
  if (!className) return null;
  const match = /language-([\w+-]+)/.exec(className);
  return match ? match[1] : null;
}

const LEGACY_HTML_PIPELINE = {
  remarkPlugins: [remarkBreaks],
  remarkRehypeOptions: { allowDangerousHtml: true } as const,
  rehypePlugins: [rehypeRaw],
  urlTransform: chatMarkdownUrlTransform,
};

function getOrderedListMarkerWidthCh(start: unknown, children: ReactNode): number {
  const startNum = start != null && !Number.isNaN(Number(start)) ? Number(start) : 1;
  let itemCount = 0;
  let maxNum = startNum;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    itemCount += 1;
    const value = (child.props as { value?: unknown })?.value;
    if (value != null && !Number.isNaN(Number(value))) {
      maxNum = Math.max(maxNum, Number(value));
    }
  });

  if (itemCount > 0) {
    maxNum = Math.max(maxNum, startNum + itemCount - 1);
  }

  return Math.max(2, String(maxNum).length + 1);
}

function ChatMermaidError({ error, chart }: MermaidErrorComponentProps) {
  return (
    <div className="chat-markdown-mermaid my-3">
      <p className="text-sm text-zinc-700">Could not render this diagram.</p>
      <p className="mt-1 text-xs text-zinc-500">{error}</p>
      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-md border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-800">
        {chart}
      </pre>
    </div>
  );
}

const streamdownComponents: Components = {
  img: (props) => {
    const { className, alt, src, srcSet, ...rest } = omit(props, ['node', 'children']);
    const srcNorm = normalizeUrlProp(src);
    const srcSetNorm = srcSet != null ? String(srcSet) : undefined;
    return (
      <img
        {...rest}
        src={srcNorm}
        srcSet={srcSetNorm}
        alt={alt != null ? String(alt) : ''}
        className={['inline-block max-w-full h-auto align-middle my-0.5', className]
          .filter(Boolean)
          .join(' ')}
        decoding="async"
        referrerPolicy="no-referrer"
      />
    );
  },
  a: (props) => {
    const { children } = props;
    const rest = omit(props, ['node', 'children']);
    return (
      <a
        {...rest}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#007bff',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </a>
    );
  },
  pre: ({ children }): ReactNode => {
    if (isValidElement(children)) {
      const { className } = children.props as { className?: string };
      if (languageFromClassName(className) === 'mermaid') {
        return cloneElement(children as ReactElement<{ 'data-block'?: string }>, {
          'data-block': 'true',
        });
      }
    }
    return children;
  },
  code: (props) => {
    const { className, children } = props;
    const text = String(children).replace(/\n$/, '');
    const lang = languageFromClassName(className);
    const isInline = !className && !text.includes('\n');

    const renderHtmlMediaSnippet = (source: string) => (
      <div className="chat-markdown-html-snippet my-2 max-w-full rounded-lg border border-gray-200 bg-white p-2 overflow-x-auto">
        <Markdown {...LEGACY_HTML_PIPELINE} components={streamdownComponents}>
          {source.trim()}
        </Markdown>
      </div>
    );

    if (isInline) {
      if (isDomMediaSnippet(text)) {
        return <span className="inline align-middle">{renderHtmlMediaSnippet(text)}</span>;
      }
      return (
        <code
          style={{
            background: '#f4f4f5',
            color: '#1f2937',
            padding: '2px 7px',
            borderRadius: '5px',
            border: '1px solid #e4e4e7',
            fontSize: '0.875em',
            letterSpacing: '0.02em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        >
          {children}
        </code>
      );
    }

    const renderFencedBlockAsHtml =
      /<\s*(img|svg|picture)\b/i.test(text) &&
      ((lang != null && INNER_HTML_LANGS.has(lang)) || (!lang && isDomMediaSnippet(text)));

    if (renderFencedBlockAsHtml) {
      return renderHtmlMediaSnippet(text);
    }

    if (lang === 'mermaid') {
      return <ChatMermaidPreview chart={text} />;
    }

    return (
      <SyntaxHighlighter
        language={lang || 'text'}
        style={oneLight}
        PreTag="pre"
        showLineNumbers={false}
        customStyle={{
          margin: '0.75em 0',
          padding: '14px 18px',
          paddingTop: '40px',
          borderRadius: '10px',
          fontSize: '13.5px',
          lineHeight: 1.65,
          letterSpacing: '0.02em',
          maxWidth: '100%',
          background: '#f7f7f8',
          border: '1px solid #e8e8ea',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          color: '#1e1e1e',
        }}
        codeTagProps={{
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Code", Consolas, monospace',
            letterSpacing: '0.02em',
          },
        }}
      >
        {text}
      </SyntaxHighlighter>
    );
  },
  ol: ({ start, children, ...props }) => {
    const markerWidthCh = getOrderedListMarkerWidthCh(start, children);
    return (
      <ol
        {...omit(props, ['node', 'children'])}
        start={start}
        className="chat-markdown-ol my-2 space-y-1"
        style={{ '--ol-marker-ch': markerWidthCh } as CSSProperties}
      >
        {children}
      </ol>
    );
  },
  table: ({ children }) => (
    <div className="chat-markdown-table-scroll my-3 max-w-full overflow-x-auto">
      <table className="min-w-full border-collapse border border-zinc-200 text-sm">
        {children}
      </table>
    </div>
  ),
  thead: (props) => <thead className="bg-zinc-100" {...omit(props, ['node'])} />,
  tbody: (props) => <tbody {...omit(props, ['node'])} />,
  tr: (props) => <tr className="even:bg-zinc-50/60" {...omit(props, ['node'])} />,
  th: (props) => (
    <th
      className="border border-zinc-200 px-3 py-2 text-left font-semibold text-zinc-900"
      {...omit(props, ['node'])}
    />
  ),
  td: (props) => (
    <td
      className="border border-zinc-200 px-3 py-2 align-top text-zinc-800"
      {...omit(props, ['node'])}
    />
  ),
};

export type ChatMarkdownStreamdownProps = {
  preparedContent: string;
  className?: string;
  isStreamAnimating: boolean;
  showCaret: boolean;
};

function ChatMarkdownStreamdown({
  preparedContent,
  className,
  isStreamAnimating,
  showCaret,
}: ChatMarkdownStreamdownProps) {
  return (
    <div
      className={['chat-markdown-streamdown', className].filter(Boolean).join(' ')}
      style={{
        overflowWrap: 'break-word',
        wordBreak: 'normal',
      }}
    >
      {preparedContent ? (
        <Streamdown
          isAnimating={isStreamAnimating}
          plugins={{ code, mermaid }}
          remarkPlugins={[remarkGfm, remarkBreaks]}
          tableMaxHeight={0}
          mermaid={{
            config: {
              theme: 'base',
              themeVariables: {
                primaryColor: '#0A8BC7',
                primaryTextColor: '#0f172a',
                primaryBorderColor: '#0A8BC7',
                lineColor: '#71717a',
                secondaryColor: '#f4f4f5',
                tertiaryColor: '#ffffff',
              },
            },
            errorComponent: ChatMermaidError,
          }}
          components={streamdownComponents}
        >
          {preparedContent}
        </Streamdown>
      ) : null}
      {showCaret ? <span className="typing-cursor">▍</span> : null}
    </div>
  );
}

ChatMarkdownStreamdown.defaultProps = {
  className: '',
};

export default ChatMarkdownStreamdown;
